import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAgeVerificationStore, AgeVerificationStatus, } from "../store/AgeVerificationStore";
import { useProfileAuthStore } from "../store/ProfileStore";
import { useCacheBusterStore } from "../util/UrlUtils";
import { useLogger } from "../logger";
const LOGPREFIX = "useAgeVerificationGating: ";
/**
 * Custom hook for managing age verification gating across the application.
 * Provides utilities to check verification status and redirect users when needed.
 */
export function useAgeVerificationGating() {
    var _a, _b, _c;
    const navigate = useNavigate();
    const auth = useProfileAuthStore();
    const ageStore = useAgeVerificationStore();
    const cacheBuster = useCacheBusterStore();
    const logger = useLogger();
    const [hasLoadedStatus, setHasLoadedStatus] = useState(false);
    // Load age verification status on mount (if user is signed in)
    useEffect(() => {
        if (auth.isSignedIn() &&
            auth.userId &&
            !hasLoadedStatus &&
            !ageStore.loading) {
            logger.log(LOGPREFIX + "Loading age verification status for user: " + auth.userId);
            ageStore.loadStatus(auth.userId, auth, cacheBuster, logger).then(() => {
                setHasLoadedStatus(true);
            });
        }
    }, [auth.isSignedIn(), auth.userId, hasLoadedStatus, ageStore.loading]);
    // Determine if user is verified
    const isVerified = ((_a = ageStore.metadata) === null || _a === void 0 ? void 0 : _a.status) === AgeVerificationStatus.VERIFIED &&
        ((_b = ageStore.metadata) === null || _b === void 0 ? void 0 : _b.isAdult) === true;
    // Check if user needs verification for adult content
    const needsVerification = (hasAdultContent) => {
        if (!hasAdultContent)
            return false;
        if (!auth.isSignedIn())
            return true; // Unsigned users need to be redirected anyway
        return !isVerified;
    };
    // Redirect to age verification page with optional return URL
    const redirectToVerification = (returnUrl) => {
        const currentUrl = returnUrl || window.location.pathname + window.location.search;
        const verificationUrl = `/AgeVerification?returnUrl=${encodeURIComponent(currentUrl)}`;
        logger.log(LOGPREFIX + "Redirecting to age verification: " + verificationUrl);
        navigate(verificationUrl);
    };
    // Check if verification is needed and redirect if so
    const checkAndRedirectIfNeeded = (hasAdultContent, returnUrl) => {
        if (needsVerification(hasAdultContent)) {
            redirectToVerification(returnUrl);
            return true; // Indicates a redirect occurred
        }
        return false; // No redirect needed
    };
    return {
        isVerified,
        isLoading: ageStore.loading || !hasLoadedStatus,
        verificationStatus: (_c = ageStore.metadata) === null || _c === void 0 ? void 0 : _c.status,
        needsVerification,
        redirectToVerification,
        checkAndRedirectIfNeeded,
    };
}
